import { useState } from 'react';
import { FundraisingFund, FUND_STAGES, FundStage } from '../../types';
import KanbanCard from './KanbanCard';
import { EyeOff, Eye } from "lucide-react";

interface KanbanBoardProps {
  funds: FundraisingFund[];
  onStageChange: (fundId: string, newStage: string) => void;
  onEditFund: (fund: FundraisingFund) => void;
  onViewFund: (fund: FundraisingFund) => void;
}

export default function KanbanBoard({ funds, onStageChange, onViewFund }: KanbanBoardProps) {
  const [draggedFund, setDraggedFund] = useState<FundraisingFund | null>(null);
  const [showUninterested, setShowUninterested] = useState(false);

  const activeFunds = funds.filter(f => f.stage !== 'uninterested');
  const uninterestedFunds = funds.filter(f => f.stage === 'uninterested');

  const groupedFunds = FUND_STAGES.reduce((acc, { stage }) => {
    acc[stage] = activeFunds.filter(f => f.stage === stage);
    return acc;
  }, {} as Record<FundStage, FundraisingFund[]>);

  const handleDragStart = (e: React.DragEvent, fund: FundraisingFund) => {
    setDraggedFund(fund);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStage: FundStage) => {
    e.preventDefault();
    if (draggedFund && draggedFund.stage !== targetStage) {
      onStageChange(draggedFund.id, targetStage);
    }
    setDraggedFund(null);
  };

  const handleDragEnd = () => {
    setDraggedFund(null);
  };

  const getColumnTotal = (stage: FundStage) => {
    const stageFunds = groupedFunds[stage] || [];
    return stageFunds.reduce((sum, f) => {
      const avgTicket = ((f.typicalTicketMin || 0) + (f.typicalTicketMax || 0)) / 2;
      return sum + avgTicket;
    }, 0);
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="space-y-4">
      {/* Uninterested Drop Zone */}
      <div
        className={`rounded-xl border transition-colors ${
          draggedFund ? 'border-red-300 bg-red-50/50' : 'border-neutral-200 bg-neutral-50/50'
        }`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, 'uninterested')}
      >
        <button
          onClick={() => setShowUninterested(!showUninterested)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-neutral-100/50 rounded-xl transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
              Uninterested
            </span>
            <span className="text-[11px] text-neutral-400">
              {uninterestedFunds.length} funds
            </span>
          </div>
          <div className="flex items-center gap-2 text-neutral-400">
            {showUninterested ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="text-[10px]">{showUninterested ? 'Hide' : 'Show'}</span>
          </div>
        </button>

        {showUninterested && uninterestedFunds.length > 0 && (
          <div className="px-4 pb-4 border-t border-neutral-100">
            <div className="grid grid-cols-4 gap-2 mt-3">
              {uninterestedFunds.map(fund => (
                <KanbanCard
                  key={fund.id}
                  fund={fund}
                  isDragging={draggedFund?.id === fund.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onClick={() => onViewFund(fund)}
                  compact
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-7 gap-3">
        {FUND_STAGES.map(({ stage, label, color, bgColor }) => (
          <div
            key={stage}
            className="flex flex-col min-h-[400px]"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage)}
          >
            {/* Column Header */}
            <div className={`rounded-t-xl px-3 py-2.5 border border-b-0 border-neutral-200 ${bgColor}`}>
              <div className="flex items-center justify-between">
                <span className={`text-[11px] uppercase tracking-wider font-medium ${color}`}>
                  {label}
                </span>
                <span className="text-[10px] text-neutral-400 bg-white/50 px-1.5 py-0.5 rounded">
                  {groupedFunds[stage]?.length || 0}
                </span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {formatCurrency(getColumnTotal(stage))}
              </div>
            </div>

            {/* Column Body */}
            <div className={`flex-1 bg-neutral-50/50 rounded-b-xl border border-neutral-200 p-2 space-y-2 ${
              draggedFund && draggedFund.stage !== stage ? 'ring-2 ring-blue-200' : ''
            }`}>
              {(!groupedFunds[stage] || groupedFunds[stage].length === 0) ? (
                <div className="h-14 flex items-center justify-center text-neutral-300 text-[11px] border border-dashed border-neutral-200 rounded-lg">
                  Drop here
                </div>
              ) : (
                groupedFunds[stage].map(fund => (
                  <KanbanCard
                    key={fund.id}
                    fund={fund}
                    isDragging={draggedFund?.id === fund.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={() => onViewFund(fund)}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
